import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import theme from "theme";
import { colors } from "theme/palette";
import { useRedirectUponFetchFailure } from "utils/common";

import Loading from "components/Loading";
import Paper from "components/Paper";
import Stack from "components/Stack";
import Text from "components/Text";
import { usePageTitle } from "components/usePageTitle";

import { useSignedUser } from "entities/Auth/sdk";
import { MemoSignatureStatus } from "entities/Memo/components/Sections/MemoESignatureDrawer/constants";
import { useMemoESignProcessFinishedEvent } from "entities/Memo/hooks";
import {
  memoSignatureEmbeddedLinkCreate,
  useMemoSignatureDealNameAndMemoName,
} from "entities/Memo/sdk";

const MemoESignature = () => {
  const hasRun = useRef(false);
  const params = useParams<{
    dealId: string;
    memoId: string;
    roleId: string;
  }>();
  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);
  const roleId = String(params.roleId);

  const [embeddedLink, setEmbeddedLink] = useState<string | null>(null);
  const [isEmbeddedLinkLoading, setIsEmbeddedLinkLoading] =
    useState<boolean>(true);

  const [appliedStatus, setAppliedStatus] =
    useState<MemoSignatureStatus | null>(null);

  const navigate = useNavigate();

  const { data: user } = useSignedUser();

  const {
    data: pageData,
    isLoading: isPageDataLoading,
    error: pageDataError,
  } = useMemoSignatureDealNameAndMemoName({ dealId, memoId });

  useRedirectUponFetchFailure({
    error: pageDataError,
    loading: isPageDataLoading,
  });

  usePageTitle(
    pageData?.memo_name
      ? `Builders Patch: ${pageData.memo_name} - E-Sign Document`
      : "Builders Patch: E-Sign Document"
  );

  useEffect(() => {
    if (hasRun.current) {
      return;
    }

    hasRun.current = true;

    memoSignatureEmbeddedLinkCreate({ dealId, memoId, roleId })
      .then((data) => {
        setEmbeddedLink(data.link);
        setAppliedStatus(data.status);
      })
      .catch((error) => {
        if (error.status === 404) {
          navigate(URLS.NOT_FOUND, { replace: true });
        }
      })
      .finally(() => setIsEmbeddedLinkLoading(false));
  }, [dealId, memoId, roleId, hasRun, navigate]);

  useMemoESignProcessFinishedEvent({
    eventHandler: ({ status }: { status: MemoSignatureStatus }) => {
      setAppliedStatus(status);
      setEmbeddedLink(null);
    },
  });

  const navigateToLandingPage = useCallback(
    () => navigate(URLS.LANDING_PAGE),
    [navigate]
  );

  const navigateToLandingPageText = useMemo(
    () => (
      <Text variant="text2">
        You can return to the{" "}
        <Text
          onClick={navigateToLandingPage}
          component="span"
          variant="text2"
          color="primary"
          sx={{ cursor: "pointer" }}
        >
          homepage
        </Text>
        .
      </Text>
    ),
    [navigateToLandingPage]
  );

  const infoText = useMemo(() => {
    if (appliedStatus === MemoSignatureStatus.APPLIED) {
      return (
        <Stack gap={2} marginTop={10}>
          <Text variant="h2">You have successfully signed this document.</Text>
          <Stack>
            <Text variant="text2">
              Once all signatures have been collected you will receive an email
              with a link to download the signed document.
            </Text>
            {navigateToLandingPageText}
          </Stack>
        </Stack>
      );
    } else if (appliedStatus === MemoSignatureStatus.REJECTED) {
      return (
        <Stack gap={2} marginTop={10}>
          <Text variant="h2">You have declined to sign this document.</Text>
          <Stack>
            <Text variant="text2">The sender will be notified.</Text>
            {navigateToLandingPageText}
          </Stack>
        </Stack>
      );
    } else if (
      [
        MemoSignatureStatus.DRAFT,
        MemoSignatureStatus.OUTDATED,
        MemoSignatureStatus.TERMINATED,
      ].includes(appliedStatus as MemoSignatureStatus)
    ) {
      return (
        <Stack gap={2} marginTop={10}>
          <Text variant="h2">Signature not required.</Text>
          {navigateToLandingPageText}
        </Stack>
      );
    }
  }, [appliedStatus, navigateToLandingPageText]);

  if (_.isNil(user) || isPageDataLoading || isEmbeddedLinkLoading) {
    return <Loading open />;
  }

  return (
    <Paper
      sx={{
        padding: theme.spacing(0, 4, 1, 4),
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Stack>
        <Stack
          padding={2}
          borderRadius="8px"
          gap={1.5}
          sx={{
            background: colors.blue10,
          }}
        >
          <Stack>
            <Text variant="label" fontWeight={500} color={colors.gray80}>
              e-sign document
            </Text>
            <Text variant="h2" data-testid="page-header-name">
              {pageData?.memo_name}
            </Text>
            <Text variant="text3" color={colors.gray80}>
              {user.company_name}
            </Text>
          </Stack>

          <Stack
            sx={{
              background: colors.blue20,
              width: "fit-content",
              padding: theme.spacing(0.25, 0.75),
              borderRadius: "4px",
            }}
          >
            <Text variant="text4" fontWeight={700}>
              {pageData?.deal_name}
            </Text>
          </Stack>
        </Stack>
      </Stack>
      <Stack position="relative" flex={1}>
        {!_.isNil(embeddedLink) && (
          <iframe
            title="Signature box"
            src={embeddedLink}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: 0,
            }}
          />
        )}
        {!_.isNil(appliedStatus) && infoText}
      </Stack>
    </Paper>
  );
};

export default MemoESignature;
